import React from 'react';
import { Node } from './Node';
import { ActionCallbacks, GraphLink, GraphNode, JourneyMapGraphicProps } from '../types';
import { EventIdentValues } from '@fullstory/proto/fs/api/segment';
import { classNames } from '@fullstory/util/fmt';
import { groupBy, isEqual, mapValues } from 'lodash';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import css from '../index.module.scss';
import { ScaleLinear } from 'd3';
import { interactionDirectionOffset } from '../util';
import { int64 } from '@fullstory/proto/google/protobuf';
import { DefinedEventField } from '@fullstory/client-api';

type NodeExtensibleJourneyMapGraphicProps =
  | 'customPivotId'
  | 'customVars'
  | 'pivotDep'
  | 'deleteFilter'
  | 'direction'
  | 'filters'
  | 'getGroupByPropsDisabled'
  | 'getNamedElement'
  | 'getNamedElementTooltip'
  | 'getDefinedEvent'
  | 'getDefinedEventTooltip'
  | 'getApiEventTooltip'
  | 'getPageDefTooltip'
  | 'getPageName'
  | 'clickPivotId'
  | 'seenPivotId'
  | 'pagePivotId'
  | 'pageVars'
  | 'setFilters'
  | 'viewMetric'
  | 'elemVars';

interface NodesProps extends Pick<JourneyMapGraphicProps, NodeExtensibleJourneyMapGraphicProps> {
  canAppendInteraction: boolean;
  identValuesByHash: { [key: string]: EventIdentValues };
  isInPath: (n: GraphNode) => boolean;
  links: GraphLink[];
  maxDepth: number;
  nodes: GraphNode[];
  scaleFunction: ScaleLinear<number, number>;
  selectedNodes: { [key: string]: GraphNode };
  actionCallbacks?: ActionCallbacks;
  showOtherEventsForDepth: (depth: number) => void;
}

const Nodes: React.FC<NodesProps> = ({
  canAppendInteraction,
  customPivotId,
  customVars,
  pivotDep,
  deleteFilter,
  direction,
  filters,
  getGroupByPropsDisabled,
  getNamedElement,
  getNamedElementTooltip,
  getDefinedEvent,
  getDefinedEventTooltip,
  getApiEventTooltip,
  getPageDefTooltip,
  getPageName,
  identValuesByHash,
  isInPath,
  links,
  maxDepth,
  clickPivotId,
  seenPivotId,
  nodes,
  pagePivotId,
  pageVars,
  scaleFunction,
  selectedNodes,
  setFilters,
  actionCallbacks,
  viewMetric,
  showOtherEventsForDepth,
  elemVars,
}) => {
  const isForwardDirection = direction === JourneyMapSettings.Direction.DIRECTION_FORWARD;
  const selectedNodesList = Object.values(selectedNodes);
  const interactionColumnCounts = mapValues(
    groupBy(selectedNodesList, node => node.depth),
    (n: GraphNode[]) => n.reduce((total, node) => total + node.count, 0),
  );
  const entryCount = nodes
    .filter(n => n.depth === (isForwardDirection ? 0 : maxDepth))
    .reduce((total, node) => total + node.count, 0);
  const outgoingFlowIndicatorHeights = mapValues(
    groupBy(links, l => l.source.getId()),
    (l: GraphLink[]) => l.reduce((sum, link) => sum + link.height, 0),
  );

  return (
    <>
      {nodes.map((node: GraphNode) => {
        const identFromHash = identValuesByHash[node.identHash];
        const correspondingFilterDepth = isForwardDirection ? node.depth : maxDepth - node.depth;
        const previousColumnDepth = isForwardDirection ? node.depth - 1 : node.depth + 1;

        const selected = node.identHash == filters?.[correspondingFilterDepth]?.hash;

        // direction
        const isTop = node.depth === 0;
        const isBottom = !!maxDepth && maxDepth === node.depth;
        const isFirst = isForwardDirection ? isTop : isBottom;
        const isLast = isForwardDirection ? isBottom : isTop;

        // counts
        const nodeCount = (selectedNodes[node.getId()] ?? node).count;
        const nextCount = (selectedNodes[node.getId()] ?? node).nextCount;
        const stepCount = isFirst ? entryCount : interactionColumnCounts[previousColumnDepth] ?? 0;

        let dropOff = scaleFunction(nodeCount) - scaleFunction(nextCount);
        // give minimum height to the indicator
        if (dropOff > 0) {
          dropOff = Math.max(dropOff, scaleFunction(0));
        }

        const isNodeInPath = isInPath(node);

        const canHideEvent = (identFromHash?: EventIdentValues): boolean => {
          if (identFromHash?.type === 'pageview') {
            const pageDefId = int64.toNumber(identFromHash?.values[0]?.longValue);
            return !isEqual(pagePivotId, pageDefId);
          } else if (
            identFromHash?.type === 'custom' ||
            identFromHash?.type === DefinedEventField
          ) {
            const pivotId = identFromHash?.values[0]?.stringValue;
            return pivotId !== customPivotId;
          } else if (identFromHash?.type === 'click') {
            const pivotId = identFromHash?.values[0]?.stringValue;
            return pivotId !== clickPivotId;
          } else if (identFromHash?.type === 'seen') {
            const pivotId = identFromHash?.values[0]?.stringValue;
            return pivotId !== seenPivotId;
          }
          return false;
        };

        return (
          <g
            key={`diagram-${node.getId()}`}
            transform={`translate(${
              node.x + interactionDirectionOffset(direction, canAppendInteraction)
            }, ${node.y})`}
            className={classNames({ [css.dimmed]: !isNodeInPath })}
            onClick={e => {
              e.stopPropagation();
              if (
                (identFromHash?.type !== 'pageview' &&
                  identFromHash?.type !== 'custom' &&
                  identFromHash?.type !== DefinedEventField &&
                  identFromHash?.type !== 'click' &&
                  identFromHash?.type !== 'seen') ||
                !setFilters
              )
                return;
              if (selected) {
                deleteFilter?.(isForwardDirection ? node.depth : maxDepth - node.depth);
              } else {
                setFilters?.({
                  [isForwardDirection ? node.depth : maxDepth - node.depth]: {
                    values: identFromHash,
                    hash: node.identHash,
                  },
                });
              }
            }}
          >
            <Node
              canHideEvent={canHideEvent(identFromHash)}
              customVars={customVars}
              pivotDep={isFirst ? pivotDep : undefined}
              deleteFilter={deleteFilter}
              entryDropin={!isForwardDirection ? (!isLast ? dropOff : 0) : 0}
              exitDropoff={isForwardDirection ? (!isLast ? dropOff : 0) : 0}
              exitOffset={outgoingFlowIndicatorHeights[node.getId()] ?? 0}
              filters={filters}
              getGroupByPropsDisabled={getGroupByPropsDisabled}
              getNamedElement={getNamedElement}
              getDefinedEvent={getDefinedEvent}
              getPageDefTooltip={getPageDefTooltip}
              getNamedElementTooltip={getNamedElementTooltip}
              getDefinedEventTooltip={getDefinedEventTooltip}
              getApiEventTooltip={getApiEventTooltip}
              getPageName={getPageName}
              actionCallbacks={actionCallbacks}
              identity={identFromHash}
              isNodeInPath={isNodeInPath}
              maxDepth={maxDepth}
              node={isNodeInPath ? selectedNodes[node.getId()] ?? node : node}
              pageVars={pageVars}
              numerator={nodeCount}
              denominator={stepCount}
              isFirstStep={isFirst}
              selected={selected}
              setFilters={setFilters}
              viewMetric={viewMetric}
              showOtherEventsForDepth={(depth: number) =>
                showOtherEventsForDepth(isForwardDirection ? depth : maxDepth - depth)
              }
              elemVars={elemVars}
            />
          </g>
        );
      })}
    </>
  );
};

export { Nodes };
